import { Transaction, PrintSettings, PosConfig, KotTicket } from '../types';
import { getPrintSettingsSync, getAppSettingsSync } from './db';
import { isOn } from './appSettings';
import { shouldPrintUpiQr, upiConfigured, upiUri } from './upi';
import { formatDate, formatTime } from './datetime';

// ESC/POS byte builder for thermal receipt printers (driven over WebUSB by
// usbPrinter.ts). Produces the same receipt/KOT content as the HTML fallback,
// encoded as printer command bytes.

const ESC = 0x1b;
const GS = 0x1d;

// Thermal printers are single-byte ASCII by default; map the few non-ASCII
// glyphs we emit (₹) and drop anything else so the stream stays printable.
function asciize(s: string): string {
  return s
    .replace(/₹/g, 'Rs')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\x7F]/g, '');
}

class EscPos {
  private bytes: number[] = [];

  raw(...b: number[]): this {
    this.bytes.push(...b);
    return this;
  }

  text(s: string): this {
    const enc = new TextEncoder().encode(asciize(s));
    this.bytes.push(...Array.from(enc));
    return this;
  }

  line(s = ''): this {
    return this.text(s).raw(0x0a);
  }

  init(): this {
    return this.raw(ESC, 0x40);
  }

  align(a: 'left' | 'center' | 'right'): this {
    return this.raw(ESC, 0x61, a === 'center' ? 1 : a === 'right' ? 2 : 0);
  }

  bold(on: boolean): this {
    return this.raw(ESC, 0x45, on ? 1 : 0);
  }

  // size: width/height multiplier 1..2 (1 = normal).
  size(w: 1 | 2, h: 1 | 2): this {
    const n = ((w - 1) << 4) | (h - 1);
    return this.raw(GS, 0x21, n);
  }

  feed(lines = 1): this {
    return this.raw(ESC, 0x64, lines);
  }

  cut(): this {
    // Feed a little then partial cut.
    return this.feed(3).raw(GS, 0x56, 0x42, 0x00);
  }

  openDrawer(): this {
    return this.raw(ESC, 0x70, 0x00, 0x19, 0xfa);
  }

  // Native QR code (GS ( k, model 2). Data is plain ASCII (UPI deep links are).
  qrcode(data: string, moduleSize = 6): this {
    const bytes = Array.from(new TextEncoder().encode(asciize(data)));
    const len = bytes.length + 3;
    const pL = len & 0xff;
    const pH = (len >> 8) & 0xff;
    return this
      // model 2
      .raw(GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00)
      // module size
      .raw(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, moduleSize)
      // error correction M
      .raw(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31)
      // store data
      .raw(GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30, ...bytes)
      // print
      .raw(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30);
  }

  build(): Uint8Array {
    return new Uint8Array(this.bytes);
  }
}

// Layout helpers for a fixed character width (32 for 58mm, 48 for 80mm).
const widthChars = (size: PrintSettings['paperSize']): number =>
  size === '2 Inch' ? 32 : 48;

// Left text + right text padded to fill the line width.
function row(left: string, right: string, width: number): string {
  const l = asciize(left);
  const r = asciize(right);
  const space = Math.max(1, width - l.length - r.length);
  if (l.length + r.length >= width) return (l + ' ' + r).slice(0, width);
  return l + ' '.repeat(space) + r;
}

function rule(width: number): string {
  return '-'.repeat(width);
}

// ---- Receipt ----
export function buildReceiptEscPos(
  tx: Transaction,
  config: PosConfig,
  settings: PrintSettings = getPrintSettingsSync()
): Uint8Array {
  const s = settings;
  const width = widthChars(s.paperSize);
  const parsed = parseInt(s.decimalDigits, 10);
  const digits = Number.isFinite(parsed) ? Math.min(2, Math.max(0, parsed)) : 2;
  const cur = config.currencySymbol;
  const n = (v: number) => v.toFixed(digits);
  const money = (v: number) => `${cur} ${v.toFixed(digits)}`;

  const title = s.title || config.storeName;
  const address = s.address || config.address || '';
  const gst = s.gstNo || config.gstin || '';
  const contact = s.contactNo || config.phone || '';
  const totalQty = tx.items.reduce((a, i) => a + i.quantity, 0);

  const p = new EscPos().init();

  // Header (centered)
  p.align('center').bold(true).size(2, 2).line(title).size(1, 1).bold(false);
  if (s.subTitle) p.line(s.subTitle);
  if (address) p.line(address);
  if (s.fssai) p.line(`FSSAI : ${s.fssai}`);
  if (gst) p.line(`GSTIN : ${gst}`);
  if (contact) p.line(`CONTACT NO. : ${contact}`);
  if (s.email) p.line(`E-Mail : ${s.email}`);
  // Estimate / non-GST bills carry a distinct document title.
  p.line(tx.estimate ? 'ESTIMATE' : tx.gstFlag === false ? 'BILL OF SUPPLY' : s.taxInvoice || 'TAX INVOICE');
  const app = getAppSettingsSync();
  if (isOn(app, 'customer_details_show_on_header') && tx.customerName) {
    p.bold(true).line(`CUSTOMER : ${tx.customerName}`).bold(false);
  }
  if (tx.orderType === 'parcel' && s.parcelName) p.line(s.parcelName);
  if (tx.orderType === 'self') p.line('SELF SERVICE');
  if (tx.table) p.line(`TABLE : ${tx.table}`);
  if (tx.token) {
    // "D - Token Number Print" — token in large type for counter pickup.
    if (isOn(app, 'token_number_print')) p.bold(true).size(2, 2).line(`TOKEN : ${tx.token}`).size(1, 1).bold(false);
    else p.line(`TOKEN : ${tx.token}`);
  }
  if (tx.counter) p.line(`COUNTER : ${tx.counter}`);
  if (tx.premise) p.line(`PREMISE : ${tx.premise}`);
  if (isOn(app, 'enable_day_night')) {
    const h = new Date(tx.timestamp).getHours();
    p.line(`SHIFT : ${h >= 6 && h < 18 ? 'DAY' : 'NIGHT'}`);
  }
  if (isOn(app, 'coupon_kot_header_on_bill') && tx.kotRemark) p.line(`KOT : ${tx.kotRemark}`);
  if (isOn(app, 'coupon_kot_header_on_bill') && tx.couponCode) p.line(`COUPON : ${tx.couponCode}`);

  // Bill meta (left)
  p.align('left');
  p.line(row(`BILL NO ${tx.billNo}`, formatDate(tx.timestamp), width));
  if (tx.invoiceNumber) p.line(`INVOICE NO ${tx.invoiceNumber}`);
  if (tx.deliveryChallanNo) p.line(`CHALLAN NO ${tx.deliveryChallanNo}`);
  if (tx.vehicleNo) p.line(`VEHICLE ${tx.vehicleNo}`);
  p.line(row(s.serialNo ? `SERIAL ${s.serialNo}` : '', formatTime(tx.timestamp), width));
  if (tx.customerName) p.line(`Customer: ${tx.customerName}`);
  if (tx.userName) p.line(`Cashier: ${tx.userName}`);

  // Items
  p.line(rule(width));
  p.line(row('ITEM', 'QTY  RATE   AMT', width));
  p.line(rule(width));
  tx.items.forEach((i) => {
    p.line(i.name);
    const meta: string[] = [];
    if (i.attributeText) meta.push(i.attributeText);
    if (i.weight != null) meta.push(`WT ${n(i.weight)}${i.unitName ? ' ' + i.unitName : ''}`);
    if (i.bags != null) meta.push(`BAGS ${n(i.bags)}`);
    if (meta.length) p.line(`  ${meta.join('  ')}`);
    p.line(row('', `${n(i.quantity)}  ${n(i.unitPrice)}  ${n(i.total)}`, width));
  });
  p.line(rule(width));

  // Totals
  p.line(row('TOTAL QTY', n(totalQty), width));
  p.line(row('AMOUNT', money(tx.subtotal), width));
  if (tx.discount) p.line(row('Discount', money(tx.discount), width));
  if (tx.serviceCharge) p.line(row('Service Charge', money(tx.serviceCharge), width));
  if (tx.tax) p.line(row('Tax', money(tx.tax), width));
  if (tx.cess) p.line(row('CESS', money(tx.cess), width));
  if (tx.parcelCharges) p.line(row('Delivery', money(tx.parcelCharges), width));
  if (tx.extraCharges) p.line(row('Extra Charges', money(tx.extraCharges), width));
  if (tx.loyaltyDiscount) p.line(row('Loyalty', `-${money(tx.loyaltyDiscount)}`, width));
  if (tx.roundOff) p.line(row('Round Off', money(tx.roundOff), width));
  p.line(rule(width));
  p.bold(true).size(1, 2).line(row('TOTAL', money(tx.total), width)).size(1, 1).bold(false);
  // Split-tender breakdown, else single Paid line.
  if (tx.payments && tx.payments.length > 1) {
    tx.payments.forEach((pmt) =>
      p.line(row(pmt.method.toUpperCase(), money(pmt.amount), width))
    );
  } else if (tx.amountPaid != null) {
    p.line(row('Paid', money(tx.amountPaid), width));
  }
  if (tx.change) p.line(row('Change', money(tx.change), width));
  if (tx.loyaltyRedeemed) p.line(row('Points Redeemed', n(tx.loyaltyRedeemed), width));
  if (tx.loyaltyEarned) p.line(row('Points Earned', n(tx.loyaltyEarned), width));
  p.line(rule(width));

  const modeLabel =
    tx.payments && tx.payments.length > 1
      ? 'SPLIT'
      : (tx.paymentMethod || 'cash').toUpperCase();
  p.align('center').line(`PAYMENT MODE : ${modeLabel}`);
  if (shouldPrintUpiQr(tx, config)) {
    p.line('SCAN TO PAY (UPI)');
    p.qrcode(upiUri(config, tx.total, `Bill ${tx.billNo}`));
    if (config.upiId) p.line(config.upiId);
  }
  if (s.extraCharge) p.line(s.extraCharge);
  p.line('POWERED BY asnasoftware CONNECT');
  if (!s.doNotPrintFooters) {
    [s.footer1, s.footer2, s.footer3, s.footer4, s.footer5].filter(Boolean).forEach((f) => p.line(f));
  }

  // "G - Disable Auto Paper Cutter" — feed without firing the cutter.
  if (isOn(app, 'disable_auto_paper_cutter')) p.feed(3);
  else p.cut();
  if (s.openCashDrawer) p.openDrawer();
  return p.build();
}

// ---- Test page ----
export function buildTestEscPos(storeName: string): Uint8Array {
  const p = new EscPos().init();
  p.align('center').bold(true).size(2, 2).line('TEST PRINT').size(1, 1).bold(false);
  p.line(storeName || 'EPOS');
  p.line(new Date().toLocaleString());
  p.line('--------------------------------');
  p.align('left').line('USB printer connected OK.');
  p.line('ESC/POS output is working.');
  p.cut();
  return p.build();
}

// ---- Coupon slip (8 — Coupon Setting) ----
// Same content as buildCouponHtml: token + items handed to the customer.
// Honors UPI QR, qty&price format, bill-no/serial and double print.
export function buildCouponEscPos(
  tx: Transaction,
  config: PosConfig,
  settings: PrintSettings = getPrintSettingsSync()
): Uint8Array {
  const app = getAppSettingsSync();
  const on = (k: string) => isOn(app, k);
  const width = widthChars(settings.paperSize);
  const cur = config.currencySymbol;
  const showPrice = on('new_coupon_format_qty_price');
  // "E. Double Coupon Print" — two copies of the slip.
  const copies = on('double_coupon_print') ? 2 : 1;

  const p = new EscPos().init();
  for (let c = 0; c < copies; c++) {
    p.align('center').bold(true).line(settings.title || config.storeName);
    p.size(2, 2).line('COUPON').size(1, 1).bold(false);
    if (tx.token) p.bold(true).size(2, 2).line(`TOKEN : ${tx.token}`).size(1, 1).bold(false);
    if (on('kot_billno_sno')) p.line(`BILL NO ${tx.billNo}`);
    p.line(`${formatDate(tx.timestamp)} ${formatTime(tx.timestamp)}`);
    p.align('left').line(rule(width));
    tx.items.forEach((i, idx) => {
      const sno = on('kot_billno_sno') ? `${idx + 1}) ` : '';
      const right = showPrice ? `${i.quantity} x ${cur}${i.unitPrice.toFixed(2)}` : String(i.quantity);
      p.line(row(`${sno}${i.name}`, right, width));
      if (i.attributeText) p.line(`  ${i.attributeText}`);
    });
    p.line(rule(width));
    if (showPrice) p.bold(true).line(row('TOTAL', `${cur} ${tx.total.toFixed(2)}`, width)).bold(false);
    if (on('coupon_upi') && upiConfigured(config)) {
      p.align('center').line('SCAN TO PAY (UPI)');
      p.qrcode(upiUri(config, tx.total, `Bill ${tx.billNo}`));
      p.align('left');
    }
    if (on('disable_auto_paper_cutter')) p.feed(3);
    else p.cut();
  }
  return p.build();
}

// ---- Shift End summary slip ----
// Thermal version of the Shift End report (label/value rows with rules).
export function buildShiftEndEscPos(
  storeName: string,
  label: string,
  dateStr: string,
  rows: [string, string][],
  settings: PrintSettings = getPrintSettingsSync()
): Uint8Array {
  const width = widthChars(settings.paperSize);
  const p = new EscPos().init();
  p.align('center').bold(true).size(1, 2).line(storeName).size(1, 1);
  p.line(`${label} END`).bold(false);
  p.line(dateStr);
  p.align('left').line(rule(width));
  rows.forEach(([l, r]) => {
    if (l === '-') p.line(rule(width));
    else p.line(row(l, r, width));
  });
  p.line(rule(width));
  const app = getAppSettingsSync();
  if (isOn(app, 'disable_auto_paper_cutter')) p.feed(3);
  else p.cut();
  return p.build();
}

// ---- Day Close summary slip ----
// Whole-day consolidated totals across ALL shifts of the day (printed on the
// ShiftEnd "Day End" action). Same label/value layout as the shift-end slip
// but headed "DAY CLOSE".
export function buildDayCloseEscPos(
  storeName: string,
  dateStr: string,
  rows: [string, string][],
  settings: PrintSettings = getPrintSettingsSync()
): Uint8Array {
  const width = widthChars(settings.paperSize);
  const p = new EscPos().init();
  p.align('center').bold(true).size(1, 2).line(storeName).size(1, 1);
  p.line('DAY CLOSE').bold(false);
  p.line(dateStr);
  p.align('left').line(rule(width));
  rows.forEach(([l, r]) => {
    if (l === '-') p.line(rule(width));
    else p.line(row(l, r, width));
  });
  p.line(rule(width));
  const app = getAppSettingsSync();
  if (isOn(app, 'disable_auto_paper_cutter')) p.feed(3);
  else p.cut();
  return p.build();
}

// ---- Generic tabular report (Reports page → USB print) ----
// Lays the report's columns across the thermal line width, last column
// right-aligned (totals/amounts), names truncated to fit. Mirrors the on-screen
// report table closely enough to read on 58mm / 80mm paper.
export function buildReportEscPos(
  title: string,
  subtitle: string,
  columns: string[],
  rows: (string | number)[][],
  settings: PrintSettings = getPrintSettingsSync()
): Uint8Array {
  const width = widthChars(settings.paperSize);
  const cols = Math.max(1, columns.length);

  // Spread columns across the line (single space between them). Any remainder
  // goes to the first column, which is usually the longest (name) field.
  const usable = width - (cols - 1);
  const base = Math.max(4, Math.floor(usable / cols));
  const widths = Array(cols).fill(base);
  widths[0] += usable - base * cols;

  const fmtRow = (cells: (string | number)[]): string =>
    columns
      .map((_, i) => {
        const s = asciize(String(cells[i] ?? ''));
        const w = widths[i];
        if (s.length > w) return s.slice(0, w);
        // Right-align the last column (amounts), left-align the rest.
        return i === cols - 1 ? s.padStart(w) : s.padEnd(w);
      })
      .join(' ');

  const p = new EscPos().init();
  p.align('center').bold(true).size(1, 2).line(asciize(title)).size(1, 1).bold(false);
  if (subtitle) p.line(asciize(subtitle));
  p.align('left').line(rule(width));
  p.bold(true).line(fmtRow(columns)).bold(false);
  p.line(rule(width));
  if (rows.length === 0) {
    p.align('center').line('No data in range.').align('left');
  } else {
    rows.forEach((r) => p.line(fmtRow(r)));
  }
  p.line(rule(width));
  const app = getAppSettingsSync();
  if (isOn(app, 'disable_auto_paper_cutter')) p.feed(3);
  else p.cut();
  return p.build();
}

// ---- KOT (kitchen ticket) ----
// Formatting honors the "16 — KOT Setting / KOT Print Option" app toggles
// (single-letter qty, weight qty, bill-no & s.no, amount column, spacing,
// large font, header suppression, total-qty line).
export function buildKotEscPos(
  kot: KotTicket,
  storeName: string,
  settings: PrintSettings = getPrintSettingsSync(),
  // Kitchen / service-point name when the ticket was split per station.
  station?: string
): Uint8Array {
  const app = getAppSettingsSync();
  const on = (k: string) => isOn(app, k);
  const width = widthChars(settings.paperSize);
  const totalQty = kot.lines.reduce((q, l) => q + l.quantity, 0);

  // "2 - Qty Show in Weight" prints quantities with 3 decimals (kg-style).
  const qty = (q: number) => (on('kot_qty_show_weight') ? q.toFixed(3) : String(q));
  const spaceLess = on('kot_space_less');
  const itemH: 1 | 2 = on('kot_large_font') ? 2 : spaceLess ? 1 : 2;
  const itemW: 1 | 2 = on('kot_large_font') ? 2 : 1;

  // Store heading: the Print Settings → Header → Title (same as the receipt),
  // falling back to the configured store name.
  const heading = settings.title || storeName;
  const p = new EscPos().init();
  if (on('kot_add_space_top')) p.feed(2); // "6 - Add Space (Top)"
  p.align('center').bold(true);
  if (!on('kot_disable_new_header')) p.size(2, 2).line('KOT').size(1, 1); // "8 - Disable NEW KOT (Header)"
  if (heading) p.line(heading);
  if (station) p.line(`STATION: ${station}`);
  p.bold(false);
  p.align('left');
  p.line(row(`KOT #${kot.kotNo}`, kot.tableName, width));
  p.line(row(`Waiter: ${kot.waiter || '-'}`, formatTime(kot.timestamp), width));
  if (kot.status === 'cancelled') p.bold(true).line('** CANCELLED **').bold(false);
  p.line(rule(width));
  p.size(itemW, itemH);
  // Double-width font halves the characters that fit on a line.
  const itemRowWidth = itemW === 2 ? Math.floor(width / 2) : width;
  kot.lines.forEach((l, idx) => {
    // Bill-style layout: item name on the left, qty on the right.
    // "3 - Bill No & S.NO" numbers the lines.
    const sno = on('kot_billno_sno') ? `${idx + 1}) ` : '';
    const right = on('kot_print_amount_column')
      ? `${qty(l.quantity)}  ${(l.quantity * l.unitPrice).toFixed(2)}`
      : qty(l.quantity);
    p.line(row(`${sno}${l.name}`, right, itemRowWidth));
    if (l.remark) p.size(1, 1).line(`   (${l.remark})`).size(itemW, itemH);
    if (on('kot_add_space') && !spaceLess) p.line(); // "5 - Add Space"
  });
  p.size(1, 1).line(rule(width));
  if (on('kot_show_product_total_qty')) p.line(row('TOTAL QTY', qty(totalQty), width)); // "9 - Show Product Total Qty"
  if (on('disable_auto_paper_cutter')) p.feed(3);
  else p.cut();
  return p.build();
}

// ---- KOT as HTML (browser-print fallback for "5 - KOT Print To External
// Printer" setups without a USB printer). Honors the same print options.
export function buildKotHtml(kot: KotTicket, storeName: string, station?: string): string {
  const app = getAppSettingsSync();
  const on = (k: string) => isOn(app, k);
  // Store heading: Print Settings → Header → Title (same as the receipt),
  // falling back to the configured store name.
  const heading = getPrintSettingsSync().title || storeName;
  const qty = (q: number) => (on('kot_qty_show_weight') ? q.toFixed(3) : String(q));
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const totalQty = kot.lines.reduce((q, l) => q + l.quantity, 0);

  const rows = kot.lines
    .map((l, idx) => {
      // Bill-style layout: item name on the left, qty on the right.
      const sno = on('kot_billno_sno') ? `${idx + 1}) ` : '';
      const right = on('kot_print_amount_column')
        ? `${qty(l.quantity)}&nbsp;&nbsp;${(l.quantity * l.unitPrice).toFixed(2)}`
        : qty(l.quantity);
      return `<div class="row item"><span>${esc(`${sno}${l.name}`)}</span><span>${right}</span></div>${
        l.remark ? `<div class="rmk">(${esc(l.remark)})</div>` : ''
      }${on('kot_add_space') && !on('kot_space_less') ? '<div class="sp"></div>' : ''}`;
    })
    .join('');

  return `<!doctype html>
<html><head><meta charset="utf-8" /><title>KOT #${kot.kotNo}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  body { margin: 0; font-family: 'Courier New', monospace; color: #000; }
  .kot { width: 80mm; padding: ${on('kot_add_space_top') ? '24px' : '6px'} 8px 6px; font-size: ${
    on('kot_space_less') ? '12px' : '13px'
  }; line-height: ${on('kot_space_less') ? 1.25 : 1.5}; }
  .hdr { text-align: center; font-weight: 700; font-size: 20px; }
  .ctr { text-align: center; }
  .row { display: flex; justify-content: space-between; }
  .item { font-size: ${on('kot_large_font') ? '19px' : '15px'}; font-weight: 700; }
  .rmk { font-size: 12px; padding-left: 12px; }
  .sp { height: 8px; }
  .hr { border-top: 1px dashed #000; margin: 4px 0; }
</style></head>
<body onload="window.print();"><div class="kot">
  ${on('kot_disable_new_header') ? '' : '<div class="hdr">KOT</div>'}
  ${heading ? `<div class="ctr">${esc(heading)}</div>` : ''}
  ${station ? `<div class="ctr">STATION: ${esc(station)}</div>` : ''}
  <div class="row"><span>KOT #${kot.kotNo}</span><span>${esc(kot.tableName)}</span></div>
  <div class="row"><span>Waiter: ${esc(kot.waiter || '-')}</span><span>${formatTime(kot.timestamp)}</span></div>
  <div class="hr"></div>
  ${rows}
  <div class="hr"></div>
  ${on('kot_show_product_total_qty') ? `<div class="row"><span>TOTAL QTY</span><span>${qty(totalQty)}</span></div>` : ''}
</div></body></html>`;
}
